function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { forwardRef, useMemo } from 'react';
import cn from 'clsx';
import { string, node, bool, func, object, objectOf, shape } from 'prop-types';
import { useForm } from '../../hooks/index.js';
import FormFeedback from '../../atoms/FormFeedback/index.jsx';
import FormInput from '../../atoms/FormInput/index.jsx';
import FormLabel from '../../atoms/FormLabel/index.jsx';
import FormGroup from '../../molecules/FormGroup/index.jsx';
import FormContext from '../../context/FormContext.js';
import FormInputGroup from '../../molecules/FormInputGroup/index.jsx';
import FormCheckbox from '../../atoms/FormCheckbox/index.jsx';
import FormToggle from '../../atoms/FormToggle/index.jsx';
import FormSwitch from '../../atoms/FormSwitch/index.jsx';
import FormSubmitButton from '../../molecules/FormSubmitButton/index.jsx';
import FormSortableInputs from '../../molecules/FormSortableInputs/index.jsx';
import FormRange from '../../atoms/FormRange/index.jsx';
import FormImageUpload from '../../molecules/FormImageUpload/index.jsx';
import FormMultiSelect from '../../molecules/FormMultiSelect/index.jsx';
import FormCheckboxSelect from '../../molecules/FormCheckboxSelect/index.jsx';
import FormRadioTabs from '../../molecules/FormRadioTabs/index.jsx';
import FormAutocomplete from '../../molecules/FormAutocomplete/index.jsx';

/**
 * Form component.
 */
const Form = /*#__PURE__*/forwardRef(({
  children,
  className,
  inverted,
  mobileSquareBorders,
  action,
  method,
  role,
  onSubmit,
  errors: initialErrors,
  messages: initialMessages,
  defaultMessages,
  defaultValues,
  onChange,
  reactBeautifulDnd,
  reactEasyCrop,
  dataFormName,
  ...restProps
}, ref) => {
  const {
    formRef,
    errors,
    validateInput,
    handleSubmit
  } = useForm({
    initialRef: ref,
    initialErrors,
    initialMessages,
    defaultMessages
  });
  const contextValue = useMemo(() => ({
    inverted,
    mobileSquareBorders,
    errors,
    validateInput,
    onChange,
    defaultValues,
    reactBeautifulDnd,
    reactEasyCrop,
    formRef
  }), [inverted, mobileSquareBorders, errors, validateInput, onChange, defaultValues, reactBeautifulDnd, reactEasyCrop, formRef]);
  return /*#__PURE__*/React.createElement(FormContext.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement("form", _extends({}, restProps, {
    noValidate: true,
    action: action,
    method: method,
    ref: formRef,
    className: cn('form', className),
    role: role,
    onSubmit: evt => handleSubmit(evt, onSubmit),
    "data-form-name": dataFormName
  }), children));
});
Form.displayName = 'Form';
Form.defaultProps = {
  children: null,
  className: null,
  inverted: false,
  mobileSquareBorders: false,
  action: null,
  method: null,
  role: null,
  onSubmit: null,
  onChange: null,
  errors: {},
  messages: {},
  defaultMessages: {},
  defaultValues: {},
  reactBeautifulDnd: null,
  reactEasyCrop: null,
  dataFormName: null
};

// See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
const validityProps = process.env.NODE_ENV !== "production" ? {
  badInput: string,
  rangeOverFlow: string,
  rangeUnderFlow: string,
  stepMismatch: string,
  tooLong: string,
  tooShort: string,
  typeMismatch: string,
  valueMissing: string
} : {};
Form.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The react-beautiful-dnd library, used by components such as FormSortableInputs. */
  reactBeautifulDnd: object,
  /** The react-easy-crop library, used by components such as FormImageUpload. */
  reactEasyCrop: func,
  /** The component's children. */
  children: node,
  /** Additional class names to add to the component root. */
  className: string,
  /** Render a form with the inverted styling. */
  inverted: bool,
  /** Use to add 0 border radius to all input for tablet down break point. */
  mobileSquareBorders: bool,
  /** The form action. */
  action: string,
  /** The form method. */
  method: string,
  /** An ARIA role. */
  role: string,
  /** On submit event handler. */
  onSubmit: func,
  /** On change event handler. */
  onChange: func,
  /** Error object containing names mapped against error messages. */
  errors: object,
  /** Custom validation messages for elements in the form. */
  messages: objectOf(shape(validityProps)),
  /** Provide a default validation messages for every field. */
  defaultMessages: shape(validityProps),
  /** The default values used to populate the form fields. The object keys are
    the names of the form fields and the values are fed to each form input as
    `defaultValue`, `defaultChecked`, or whatever similar property that
    particular input provides. */
  defaultValues: object,
  dataFormName: string
} : {};
Form.Feedback = FormFeedback;
Form.Input = FormInput;
Form.Label = FormLabel;
Form.Group = FormGroup;
Form.InputGroup = FormInputGroup;
Form.Checkbox = FormCheckbox;
Form.Toggle = FormToggle;
Form.Switch = FormSwitch;
Form.SortableInputs = FormSortableInputs;
Form.FormRange = FormRange; // TODO: Should be Form.Range
Form.SubmitButton = FormSubmitButton;
Form.ImageUpload = FormImageUpload;
Form.MultiSelect = FormMultiSelect;
Form.CheckboxSelect = FormCheckboxSelect;
Form.RadioTabs = FormRadioTabs;
Form.Autocomplete = FormAutocomplete;
export default Form;